import React from 'react';
import { Folder, Settings, Lock, Share2, User } from 'lucide-react';
import { useTodoLists } from '../hooks/useTodos';
import { useUser } from '../contexts/UserContext';
import { TodoList } from '../types/todo';
import { AssigneeDropdown } from './AssigneeDropdown';
import { Select, Group, Text, Avatar, Badge, ActionIcon, Flex, Box, Tooltip } from '@mantine/core';

interface ListDropdownProps {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  onCreateList?: () => void;
  hideAllOption?: boolean;
  onEditList?: () => void;
  selectedList?: TodoList | null;
  hideMetadata?: boolean; // Hide private/public indicator and edit button
  compactMode?: boolean; // Show only icon when value is selected, full dropdown when empty
  // Assignee props
  assigneeValue?: string;
  onAssigneeChange?: (value: string | undefined) => void;
}

export const ListDropdown: React.FC<ListDropdownProps> = ({ 
  value, 
  onChange, 
  placeholder = "Select list",
  hideAllOption = false,
  onEditList,
  selectedList,
  hideMetadata = false,
  compactMode = false,
  // Assignee props
  assigneeValue,
  onAssigneeChange
}) => {
  const { data: lists = [], isLoading } = useTodoLists();
  const { currentUser } = useUser();

  // Helper function to check if current user owns a list
  const isOwner = (list: TodoList) => {
    return currentUser?.email === list.createdBy;
  };


  // Prepare select data
  const selectData = [
    ...(hideAllOption ? [] : [{ value: 'all', label: 'All lists' }]),
    ...lists.map(list => ({
      value: list.id.toString(),
      label: list.name,
      description: list.description,
      color: list.color,
      isOwned: isOwner(list),
      createdBy: list.createdBy
    }))
  ];

  if (isLoading) {
    return (
      <Select
        placeholder="Loading lists..."
        disabled
        leftSection={<Folder size={16} />}
        size="sm"
        radius="md"
      />
    );
  }

  // Compact mode: show just a borderless folder icon that opens the dropdown
  if (compactMode) {
    const currentList = lists.find(list => list.id === value);
    return (
      <Flex gap="xs" align="center">
        <Select
          data={selectData}
          value={value?.toString() || null}
          onChange={(val) => {
            if (val === 'all' || val === null) {
              onChange(undefined);
            } else {
              onChange(parseInt(val));
            }
          }}
          placeholder=""
          renderOption={({ option }) => {
            if (option.value === 'all') {
              return (
                <Group gap="sm" wrap="nowrap" justify="space-between">
                  <Avatar size={24} radius="sm" color="gray">All</Avatar>
                  <Box flex={1}>
                    <Text size="sm" fw={500} lineClamp={1} c="var(--app-text-primary)">All lists</Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>Default todo list for new items</Text>
                  </Box>
                </Group>
              );
            }

            const listOption = selectData.find(d => d.value === option.value);
            if (!listOption || listOption.value === 'all') return option.label;

            const fullListOption = listOption as {
              value: string;
              label: string;
              description?: string;
              color?: string;
              isOwned: boolean;
              createdBy: string;
            };

            return (
              <Group gap="sm" wrap="nowrap" justify="space-between">
                <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                  <Avatar
                    size={24}
                    radius="sm"
                    style={{ backgroundColor: fullListOption.color || '#3b82f6' }}
                    styles={{
                      root: { color: 'white' },
                      placeholder: { color: 'white' }
                    }}
                  >
                    {fullListOption.label.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box flex={1}>
                    <Text size="sm" fw={500} lineClamp={1} c="var(--app-text-primary)">{fullListOption.label}</Text>
                    {fullListOption.description && (
                      <Text size="xs" c="dimmed" lineClamp={1}>{fullListOption.description}</Text>
                    )}
                  </Box>
                </Group>
              </Group>
            );
          }}
          searchable={lists.length >= 10}
          size="sm"
          radius="md"
          comboboxProps={{
            withinPortal: true,
            dropdownPadding: 8,
            position: 'bottom-end',
            middlewares: { flip: true, shift: true },
            styles: {
              dropdown: {
                minWidth: '280px',
                maxWidth: window.innerWidth <= 768 ? '85vw' : 
                         window.innerWidth <= 1024 ? '400px' : 
                         window.innerWidth <= 1440 ? '450px' : '500px',
                maxHeight: '50vh',
                overflowY: 'auto'
              }
            }
          }}
          leftSection={
            <Folder 
              size={18} 
              style={{ 
                color: currentList?.color || 'var(--app-text-secondary)',
                cursor: 'pointer'
              }} 
            />
          }
          styles={{
            root: {
              width: 'auto',
            },
            wrapper: {
              width: 'auto',
            },
            input: {
              width: '28px',
              minWidth: '28px',
              height: '32px',
              padding: '0',
              paddingLeft: '28px',
              paddingRight: '0',
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              '&:focus': {
                border: 'none',
                outline: 'none',
              }
            },
            section: {
              width: 'auto',
              pointerEvents: 'none',
            }
          }}
          rightSection={<div style={{ width: 0, height: 0, overflow: 'hidden' }} />}
          withCheckIcon={false}
        />
      </Flex>
    );
  }

  return (
    <Flex gap="xs" align="center" style={{ width: '100%' }}>
      {/* List dropdown */}
      <Select
        data={selectData}
        value={value?.toString() || null}
        onChange={(val) => {
          if (val === 'all' || val === null) {
            onChange(undefined);
          } else {
            onChange(parseInt(val));
          }
        }}
        placeholder={placeholder}
        leftSection={<Folder size={16} />}
        renderOption={({ option }) => {
          if (option.value === 'all') {
            return (
              <Group gap="sm" wrap="nowrap" justify="space-between">
                <Avatar size={24} radius="sm" color="gray">All</Avatar>
                <Box flex={1}>
                  <Text size="sm" fw={500} lineClamp={1} c="var(--app-text-primary)">All lists</Text>
                  <Text size="xs" c="dimmed" lineClamp={1}>Default todo list for new items</Text>
                </Box>
              </Group>
            );
          }

          const listOption = selectData.find(d => d.value === option.value);
          if (!listOption || listOption.value === 'all') return option.label;

          // Type guard to ensure we have the full list option with all properties
          const fullListOption = listOption as {
            value: string;
            label: string;
            description?: string;
            color?: string;
            isOwned: boolean;
            createdBy: string;
          };

          return (
            <Group gap="sm" wrap="nowrap" justify="space-between">
              <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                <Avatar
                  size={24}
                  radius="sm"
                  style={{ backgroundColor: fullListOption.color || '#3b82f6' }}
                  styles={{
                    root: { color: 'white' },
                    placeholder: { color: 'white' }
                  }}
                >
                  {fullListOption.label.charAt(0).toUpperCase()}
                </Avatar>
                <Box flex={1}>
                  <Text size="sm" fw={500} lineClamp={1} c="var(--app-text-primary)">{fullListOption.label}</Text>
                  {fullListOption.description && (
                    <Text size="xs" c="dimmed" lineClamp={1}>{fullListOption.description}</Text>
                  )}
                </Box>
              </Group>
              {fullListOption.isOwned ? (
                <Tooltip label="You own this list">
                  <User size={12} color="gray" />
                </Tooltip>
              ) : (
                <Tooltip label={`Shared by ${fullListOption.createdBy}`}>
                  <Share2 size={12} color="gray" />
                </Tooltip>
              )}
            </Group>
          );
        }}
        searchable={lists.length >= 10}
        size="sm"
        radius="md"
        style={{ flex: 1 }}
        comboboxProps={{
          withinPortal: true,
          dropdownPadding: 8,
          styles: {
            dropdown: {
              minWidth: '280px',
              maxWidth: window.innerWidth <= 768 ? '85vw' : 
                       window.innerWidth <= 1024 ? '400px' : 
                       window.innerWidth <= 1440 ? '450px' : '500px'
            }
          }
        }}
      />
      
      {/* Assignee dropdown - positioned right after list name */}
      {onAssigneeChange && (
        <AssigneeDropdown
          value={assigneeValue === 'unassigned' ? '__unassigned__' : (assigneeValue !== 'any' ? assigneeValue : undefined)}
          onChange={onAssigneeChange}
          placeholder="Any"
          variant="inline"
        />
      )}
      
      {/* Right section: sharing indicator and edit button */}
      {!hideMetadata && selectedList && (
        <Group gap="xs">
          {/* Sharing indicator */}
          {selectedList.isShared ? (
            <Badge
              variant="light"
              color="blue"
              size="xs"
              leftSection={<Share2 size={10} />}
              title={`Shared with: ${selectedList.sharedWith?.join(', ') || 'No users'}`}
            >
              {selectedList.sharedWith?.length || 0}
            </Badge>
          ) : (
            <Badge
              variant="light"
              color="gray"
              size="xs"
              leftSection={<Lock size={10} />}
              title="Private list"
            >
              Private
            </Badge>
          )}
          
          {/* Edit button */}
          {onEditList && (
            <ActionIcon
              variant="subtle"
              size="sm"
              onClick={onEditList}
              title={`Edit ${selectedList.name} list`}
            >
              <Settings size={12} />
            </ActionIcon>
          )}
        </Group>
      )}
    </Flex>
  );
};